import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Transition} from 'react-transition-group';
import ComponentLoading from '../../../../ComponentLoading';
import TransactionTable from '../../tabComponents/Transactions/TransactionTable';
import FixedDetailsPanel from '../../../TransactionsPage/FixedDetailsPanel';
import LoadMoreButton from '../../../TransactionsPage/LoadMoreButton';
import TransactionHeaderDetails from '../../tabComponents/Transactions/TransactionHeaderDetails';
import TransactionExpenseDetailsPanel from '../../../TransactionsPage/TransactionExpenseDetailsPanel';
import {StyledTransactionsTabContainer, StyledTableContainer, StyledTable} from './StyledTransactionTab';
import {transactionsHelpers} from '../../../../../utils/helpers';
import {SYSTEM_ANIMATION_DURATION} from '../../../../../constants';

const tableElementId = 'transactions-table';

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION});

const TransactionsTab = ({
  loading = false,
  loadMoreProps,
  transactions,
  edit = true,
  expenses,
  onExpenseUpdate,
  onSubscriptionUpdate,
  tableProps,
  subscription,
}) => {
  const [t] = useTranslation(['main', 'transactions']);
  const [storedTransactions, setStoredTransactions] = useState([]);
  const [storedExpenses, setStoredExpenses] = useState([]);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(undefined);

  const tableNodeRef = useRef(null);
  const detailsNodeRef = useRef(null);

  const employees = useSelector(state => state.company.employees);

  const getGroupedTransactions = (transactions) => {
    const daysList = {};
    const datePropName = 'created_date';
    const summarizeKeyName = 'isYearlyGrouping';
    let groupedTransactions = [];
    let lastAdded;

    // define transactions months
    transactions.forEach(transaction => {
      const date = transaction[datePropName].slice(0, 4);
      const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
      let {amount, is_incoming: isIncoming} = transaction;
      amount = isIncoming ? amount : -amount;
      if (!isReturnedTransaction) {
        if (daysList.hasOwnProperty(date)) {
          daysList[date] = {
            amount: daysList[date].amount + amount
          }
        } else {
          daysList[date] = {amount}
        }
      }
    });

    // add grouping rows to transactions list
    transactions.forEach(t => {
      const date = t[datePropName].slice(0, 4);
      if (lastAdded !== date && daysList.hasOwnProperty(date)) {
        lastAdded = date;
        groupedTransactions.push({
          isYearlyGrouping: true,
          [datePropName]: date,
          ...daysList[date]
        });
      }
      groupedTransactions.push(t);
    });
    groupedTransactions = transactionsHelpers.additionalTransactionsGrouping(groupedTransactions, summarizeKeyName);
    return groupedTransactions;
  }

  useEffect(() => {
    let storedTransactions = transactions.map(t => {
      const user = employees.find(e => e.employee_id === t.user_id);
      const expense = storedExpenses.find(e => e.id === t.expense_id);
      return {
        ...t,
        expense,
        user
      }
    });
    storedTransactions = getGroupedTransactions(storedTransactions);
    setStoredTransactions(storedTransactions);
  }, [employees, transactions, storedExpenses]);

  useEffect(() => {
    setStoredExpenses(expenses);
  }, [expenses]);

  const handleOnExpenseUpdate = (updatedExpense, actionType) => {
    if (updatedExpense) {
      let updatedExpenses = transactionsHelpers.getUpdatedBatchExpensesList(storedExpenses, updatedExpense, actionType);
      setSelectedTransaction({...selectedTransaction, expense: updatedExpense});
      setStoredExpenses(updatedExpenses);
      onExpenseUpdate && onExpenseUpdate(updatedExpense, actionType);
    }
  }

  const handleOpenDetails = (transactionId, index) => {
    const transaction = storedTransactions.find(d => d.id === transactionId);
    setSelectedTransaction(transaction);
    setIsOpenDetails(true);
    selectedRowIndex !== index && setSelectedRowIndex(index);
  }

  const handleCloseDetails = () => {
    setIsOpenDetails(false);
    selectedRowIndex && setSelectedRowIndex(undefined);
  }

  return (
    <StyledTransactionsTabContainer>
      <TransactionHeaderDetails
        data={subscription}
        transactionsCount={transactions.length}
      />
      <StyledTableContainer fullHeight={String(Boolean(storedTransactions.length))}>
        <Transition nodeRef={tableNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
          {state => (
            <StyledTable id={tableElementId} style={{ ...tableStyles.default, ...tableStyles[state] }}>
              <TransactionTable
                totalSpend={subscription?.ytg || 0}
                data={storedTransactions}
                edit={edit}
                onExpenseUpdate={onExpenseUpdate}
                onRowClick={handleOpenDetails}
                selectedRowIndex={selectedRowIndex}
                {...tableProps}
              />

              <LoadMoreButton
                {...loadMoreProps}
              >
                {t('loadMore')}
              </LoadMoreButton>
            </StyledTable>
          )}
        </Transition>
        <Transition nodeRef={detailsNodeRef} in={isOpenDetails} timeout={SYSTEM_ANIMATION_DURATION}>
          {state => (
            <FixedDetailsPanel
              open={isOpenDetails}
              tableElementId={tableElementId}
              style={{ ...detailsStyles.default, ...detailsStyles[state] }}
            >
              <TransactionExpenseDetailsPanel
                className='expense-details-panel'
                onClose={handleCloseDetails}
                onExpenseUpdate={handleOnExpenseUpdate}
                onSubscriptionUpdate={onSubscriptionUpdate}
                subscription={subscription}
                transaction={selectedTransaction}
              />
            </FixedDetailsPanel>
          )}
        </Transition>
      </StyledTableContainer>

      {loading && <ComponentLoading />}
    </StyledTransactionsTabContainer>
  );
}

TransactionsTab.propTypes = {
  edit: PropTypes.bool,
  expenses: PropTypes.array,
  loading: PropTypes.bool,
  loadMoreProps: PropTypes.object,
  onExpenseUpdate: PropTypes.func,
  onSubscriptionUpdate: PropTypes.func,
  subscription: PropTypes.object,
  tableProps: PropTypes.object,
  transactions: PropTypes.array
}

export default TransactionsTab;
